import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from './types';
import FileListItemCompact from './FileListItemCompact';
import { tss } from 'tss-react/mui';

const useStyles = tss.create(() => ({
  dialogPaper: {
    minWidth: '600px',
    maxWidth: '800px',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #EAEFF4',
  },
  titleText: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 600,
    fontSize: '18px',
    color: '#2A3547',
  },
  dialogContent: {
    padding: '24px',
    minHeight: '200px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#7C8FAC',
    fontStyle: 'italic',
  },
  dialogActions: {
    padding: '16px 24px',
    borderTop: '1px solid #EAEFF4',
  },
}));

interface FilesModalProps {
  open: boolean;
  onClose: () => void;
  files: UploadedFile[];
  onRemoveFile: (id: string) => void;
}

const FilesModal: React.FC<FilesModalProps> = ({ open, onClose, files, onRemoveFile }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}>
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          {t('agentPage.knowledgeBase.filesModal.title')} ({files.length})
        </Typography>
        <IconButton onClick={onClose} size="small">
          <IconX size={20} />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {files.length > 0 ? (
          <Box className={classes.filesList}>
            {files.map((file) => (
              <FileListItemCompact key={file.id} file={file} onRemove={onRemoveFile} />
            ))}
          </Box>
        ) : (
          <Box className={classes.emptyState}>
            <Typography>{t('agentPage.knowledgeBase.filesModal.noFiles')}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('agentPage.knowledgeBase.filesModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilesModal;
